/**
 * ContentActions
 * 
 * Displays action buttons for video/content (like, comment, gift, share).
 * Matches Flutter's ContentActions from content_widget.dart
 * 
 * Features:
 * - Like button with animation and count
 * - Comment button with count
 * - Gift/Tip button with count
 * - Share button
 * - Horizontal scrollable layout
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AnimatedBubblesButton } from './AnimatedBubblesButton';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ContentActionsProps {
  /** Content/media id — used to make per-card `data-qa-id`s page-unique. */
  contentId: string;

  /** User ID of content creator */
  userId: string;

  /** Whether current user is subscribed */
  subscribed: boolean;

  /** Like count */
  likes?: number;

  /** Comment count */
  comments?: number;

  /** Coin/gift count */
  coins?: number;

  /** Whether to display add comment */
  displayAddComment: boolean;

  /** Whether to show coins */
  showCoins: boolean;

  /** Default gift image URL (optional) */
  defaultGiftUrl?: string;

  /** Whether content is liked */
  isLiked: boolean;

  /** Background color for action buttons */
  buttonBackgroundColor?: string;

  /** Callback when user is tapped */
  onUserTap: (userId: string) => Promise<void> | void;

  /** Callback when add comment is tapped */
  onAddCommentTap: () => Promise<void> | void;

  /** Callback when like is tapped */
  onLikeTap: () => Promise<void> | void;

  /** Callback when send gift is tapped */
  onSendGiftTap: () => Promise<void> | void;

  /** Callback when more is tapped (optional - not rendered in this component) */
  onMoreTap?: () => Promise<void> | void;

  /** Callback when share is tapped */
  onShareSocialContentTap: () => Promise<void> | void;

  /** Custom CSS class */
  className?: string;
}

export const ContentActions: React.FC<ContentActionsProps> = ({
  contentId,
  userId: _userId,
  subscribed: _subscribed,
  likes = 0,
  comments = 0,
  coins = 0,
  displayAddComment: _displayAddComment,
  showCoins,
  defaultGiftUrl,
  isLiked,
  buttonBackgroundColor,
  onUserTap: _onUserTap,
  onAddCommentTap,
  onLikeTap,
  onSendGiftTap,
  onMoreTap: _onMoreTap,
  onShareSocialContentTap,
  className = ''
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const bgColor = buttonBackgroundColor

  return (
    <div
      className={`flex items-center ${className}`}
      style={{
        gap: `${responsive.gap.sm}px`,
        overflowX: 'auto',
        // Allow vertical overflow for animations
        overflowY: 'visible',
        // Transparent scrollbar
        scrollbarWidth: 'none' /* Firefox */,
        msOverflowStyle: 'none' /* IE 10+ */
      }}
    >

      {/* Like button */}
      <div
        {...qaAttr({
          id: `content-action-like-${contentId}`,
          value: isLiked ? 'liked' : 'unliked',
          status: isLiked ? 'liked' : 'unliked',
        })}
        className="flex items-center rounded-full hover:opacity-80 transition-opacity"
        style={{
          backgroundColor: bgColor,
          paddingRight: `${responsive.spacing['3xl']}px`,
          gap: `${responsive.gap.xs}px`,
          paddingTop: responsive.spacing.xs,
          paddingBottom: responsive.spacing.xs,
          overflow: 'visible', // Allow heart animations to expand
          position: 'relative'
        }}
      >
        {/* Animated Heart icon */}
        <AnimatedBubblesButton
          contentId={contentId}
          isLiked={isLiked}
          size={responsive.icon.medium}
          likeColor={theme.activeRed}
          unlikeColor={theme.icon.primary}
          onTap={onLikeTap}
          animationDuration={ANIMATION_CONSTANTS.duration.long}
          circleColor={theme.exclusive}
          iconUrl="/assets/images/heart_fill.png"
          bubbles={[
            { color: theme.activeRed },
            { color: theme.blue },
            { color: theme.exclusive },
            { color: theme.exclusive },
            { color: theme.blue },
            { color: theme.exclusive }
          ]}
        />

        {/* Like count */}
        <span
          style={{
            color: theme.icon.primary,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.medium
          }}
        >
          {MathUtils.formatCount(likes)}
        </span>
      </div>

      {/* Comment button */}
      <button
        {...qaAttr({ id: `content-action-comment-${contentId}`, value: comments })}
        onClick={onAddCommentTap}
        className="flex items-center rounded-full hover:opacity-80 transition-opacity focus:outline-none"
        style={{
          backgroundColor: bgColor,
          paddingRight: `${responsive.spacing['3xl']}px`,
          gap: `${responsive.gap.xs}px`,
          paddingTop: responsive.spacing.xs,
          paddingBottom: responsive.spacing.xs,
        }}
      >
        {/* Comment icon */}
        <AssetIcon path="/assets/vectors/comment_fill.svg"
          width={responsive.icon.medium}
          height={responsive.icon.medium} />

        {/* Comment count */}
        <span
          style={{
            color: theme.icon.primary,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.medium
          }}
        >
          {MathUtils.formatCount(comments)}
        </span>
      </button>

      {/* Gift/Tip button */}
      <button
        {...qaAttr({ id: `content-action-gift-${contentId}`, value: showCoins ? coins : undefined })}
        onClick={onSendGiftTap}
        className="flex items-center rounded-full hover:opacity-80 transition-opacity focus:outline-none"
        style={{
          backgroundColor: bgColor,
          paddingRight: `${responsive.spacing['3xl']}px`,
          gap: `${responsive.gap.xs}px`,
          paddingTop: responsive.spacing.xs,
          paddingBottom: responsive.spacing.xs,
        }}
      >
        {/* Gift icon or default gift image */}
        {defaultGiftUrl ? (
          <CachedImage
            src={defaultGiftUrl}
            alt="Gift"
            style={{
              width: `${responsive.icon.medium}px`,
              height: `${responsive.icon.medium}px`,
              objectFit: 'cover',
            }}
          />
        ) : (
          <AssetIcon path="/assets/images/tip.png" width={responsive.icon.medium} height={responsive.icon.medium} />
        )}

        {/* Coin count */}
        <span
          style={{
            color: theme.icon.primary,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.medium
          }}
        >
          {showCoins ? MathUtils.formatCount(coins) : '-'}
        </span>
      </button>

      {/* Share button */}
      <button
        {...qaAttr({ id: `content-action-share-${contentId}` })}
        onClick={onShareSocialContentTap}
        className="hover:opacity-80 transition-opacity focus:outline-none"
        style={{
          padding: responsive.spacing.xs,
        }}
      >
        <AssetIcon path="/assets/images/share_fill.png" width={responsive.icon.medium - 2} height={responsive.icon.medium - 2} />
      </button>
    </div>
  );
};
